import React, { useState, useCallback, useMemo } from "react";
import {
  Drawer,
  IconButton,
  Tooltip,
  Box,
  ButtonBase,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { alpha, styled } from "@mui/material/styles";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import TaskNotificationsBadge from "./TaskNotificationsBadge";
import SidebarUsageDisplay from "./SidebarUsageDisplay";
import HelpDialog from "./HelpDialog";
import {
  SidebarNavItem,
  SidebarNavScrollArea,
  SidebarNavSection,
  SidebarNavSectionHeader,
  SidebarNavGroup,
  SidebarNavMoreMenu,
} from "../common/sidebarNav";
import { AI_AGENTS_CLIENT_IDS, DOCS_CLIENT_IDS } from "../../constants/featureFlags";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { stringAvatar } from "../../utils/AvatarUtils";
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
  PAGE_HEADER_HEIGHT,
} from "../../constants/pageLayout";

const DRAWER_WIDTH = SIDEBAR_WIDTH;
const DRAWER_COLLAPSED_WIDTH = SIDEBAR_COLLAPSED_WIDTH;

function SidebarPanelIcon({ rotate = false }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="-1 -2 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{
        width: 16,
        height: 16,
        flexShrink: 0,
        transform: rotate ? "rotate(180deg)" : undefined,
      }}
    >
      <path d="M0.75 3.25C0.75 1.87 1.87 0.75 3.25 0.75H17.25C18.63 0.75 19.75 1.87 19.75 3.25V16.25C19.75 17.63 18.63 18.75 17.25 18.75H3.25C1.87 18.75 0.75 17.63 0.75 16.25V3.25Z" />
      <path d="M8.25 0.75V18.75" />
    </svg>
  );
}

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})(({ theme, isOpen }) => ({
  width: isOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
  flexShrink: 0,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  "& .MuiDrawer-paper": {
    position: "relative",
    width: isOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
    boxSizing: "border-box",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    whiteSpace: "nowrap",
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: 14,
  },
}));

const SidebarBody = styled(Box)({
  display: "flex",
  height: "100%",
  flexDirection: "column",
});

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  height: PAGE_HEADER_HEIGHT,
  minHeight: PAGE_HEADER_HEIGHT,
  boxSizing: "border-box",
  padding: "0 8px 0 10px",
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const BrandButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})(({ theme, isOpen }) => ({
  display: "flex",
  height: 30,
  maxWidth: isOpen ? 202 : 30,
  minWidth: 30,
  flex: isOpen ? 1 : "0 0 auto",
  alignItems: "center",
  justifyContent: isOpen ? "flex-start" : "center",
  gap: 8,
  borderRadius: 8,
  padding: isOpen ? "0 7px" : 0,
  boxSizing: "border-box",
  color: theme.palette.text.primary,
  textDecoration: "none",
  overflow: "hidden",
  transition: "background-color 150ms ease, max-width 200ms ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CollapseButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})(({ theme, isOpen }) => ({
  width: isOpen ? 30 : 0,
  height: 30,
  marginLeft: "auto",
  padding: 0,
  borderRadius: 8,
  boxSizing: "border-box",
  color: theme.palette.text.secondary,
  opacity: isOpen ? 1 : 0,
  overflow: "hidden",
  pointerEvents: isOpen ? "auto" : "none",
  transition:
    "width 200ms ease, opacity 200ms ease, background-color 150ms ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

const OrgAvatar = styled(Box, {
  shouldForwardProp: (prop) => prop !== "avatarColor",
})(({ avatarColor }) => ({
  display: "flex",
  width: 20,
  height: 20,
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  backgroundColor: avatarColor,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1,
}));

const BottomSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  flexDirection: "column",
  gap: 2,
  borderTop: `1px solid ${alpha(theme.palette.divider, 0)}`,
  padding: "9px 8px 8px",
}));

const RESOURCE_PATHS = [
  "/agents",
  "/teams",
  "/user_management",
  "/user_management_new",
  "/project",
  "/schedules",
  "/files",
  "/artifacts",
  "/docs",
  "/global_variables",
  "/credential_store",
  "/custom_functions",
  "/forms",
  "/mcp_servers",
  "/project/trash",
];

const getMoreResourceItems = ({ roleEditor, roleAdministrator }) => [
  {
    path: "/schedules",
    label: "Schedules",
    icon: <ScheduleOutlinedIcon />,
    tooltip: "View all scheduled activity across workflows and agents",
    visible: roleEditor || roleAdministrator,
  },
  {
    path: "/global_variables",
    label: "Global Variables",
    icon: <DataObjectOutlinedIcon />,
    tooltip: "Manage account-level variables accessible in all workflows",
    visible: roleEditor || roleAdministrator,
  },
];

const getMainMenuItems = ({
  roleEditor,
  roleAdministrator,
  rolePrivateViewer,
  clientId,
}) => [
  {
    path: "/",
    label: "Home",
    icon: <HomeOutlinedIcon />,
    tooltip: "Your home page",
    visible: roleEditor || rolePrivateViewer,
    exact: true,
    id: "main_menu_drawer_home",
  },
  {
    path: "/agents/chats",
    label: "Chats",
    icon: <ChatBubbleOutlineIcon />,
    tooltip: "Agent conversations",
    visible: roleEditor || roleAdministrator,
  },
  {
    path: "/boards",
    label: "Boards",
    icon: <ViewKanbanOutlinedIcon />,
    tooltip: "Agentic kanban boards",
    visible: roleEditor || roleAdministrator,
  },
  {
    path: "/missions",
    label: "Missions",
    icon: <FlagOutlinedIcon />,
    tooltip: "Top-level missions coordinating agents and resources",
    visible: roleEditor || roleAdministrator,
  },
  {
    path: "/task",
    label: "Inbox",
    icon: <TaskNotificationsBadge />,
    tooltip: "Your inbox",
    visible: true,
  },
  {
    path: "/databases",
    label: "Tables",
    icon: <TableChartOutlinedIcon />,
    tooltip: "Access your built-in tables",
    visible: roleEditor || roleAdministrator,
  },
  {
    label: "Resources",
    visible: roleEditor || roleAdministrator,
    isResourceGroup: true,
    matchPaths: RESOURCE_PATHS,
    excludePaths: ["/project/canvas", "/project/canvas_preview"],
    children: [
      {
        path: "/agents",
        label: "Agents",
        icon: <SmartToyOutlinedIcon />,
        tooltip: "Manage your AI agents",
        excludePaths: ["/agents/chats"],
        visible:
          (roleEditor || roleAdministrator) &&
          AI_AGENTS_CLIENT_IDS.includes(clientId),
      },
      {
        path: "/teams",
        label: "Teams",
        icon: <WorkspacesOutlinedIcon />,
        tooltip: "Manage multi-agent teams",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/project",
        label: "Workflows",
        icon: <AccountTreeOutlinedIcon />,
        tooltip: "All of your published and unpublished workflows",
        visible: roleEditor,
        excludePaths: [
          "/project/trash",
          "/project/canvas",
          "/project/canvas_preview",
        ],
      },
      {
        path: "/files",
        label: "Files",
        icon: <FolderOutlinedIcon />,
        tooltip: "View all files across your workflows",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/artifacts",
        label: "Artifacts",
        icon: <DynamicFormOutlinedIcon />,
        tooltip: "Manage reusable artifact definitions and generated instances",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/docs",
        label: "Docs",
        icon: <MenuBookOutlinedIcon />,
        tooltip: "Notion-like wiki for docs, playbooks, and agent skills",
        visible:
          (roleEditor || roleAdministrator) &&
          DOCS_CLIENT_IDS.includes(clientId),
      },
      {
        path: "/credential_store",
        label: "Secrets",
        icon: <VpnKeyOutlinedIcon />,
        tooltip: "View your securely stored credentials and secrets",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/custom_functions",
        label: "Custom Functions",
        icon: <CodeOutlinedIcon />,
        tooltip: "Manage reusable custom functions",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/forms",
        label: "Forms",
        icon: <DescriptionOutlinedIcon />,
        tooltip: "Create and manage reusable forms and review tasks",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/mcp_servers",
        label: "MCP Servers",
        icon: <HubOutlinedIcon />,
        tooltip: "Connect external MCP servers for your agents",
        visible: roleEditor || roleAdministrator,
      },
      {
        path: "/user_management",
        label: "Humans",
        icon: <GroupsOutlinedIcon />,
        tooltip: "Manage users in your account",
        visible: roleAdministrator,
        matchPaths: ["/user_management", "/user_management_new"],
      },
      {
        path: "/project/trash",
        label: "Trash",
        icon: <DeleteOutlineOutlinedIcon />,
        tooltip: "View deleted workflows",
        visible: roleEditor || roleAdministrator,
      },
      {
        label: "More",
        icon: <MoreHorizOutlinedIcon />,
        tooltip: "Additional resources",
        visible: roleEditor || roleAdministrator,
        isMoreMenu: true,
        matchPaths: ["/schedules", "/global_variables"],
        children: getMoreResourceItems({ roleEditor, roleAdministrator }),
      },
    ],
  },
];

function isItemActive(item, pathname) {
  if (item.exact) {
    return pathname === item.path;
  }

  if (item.excludePaths) {
    for (const excluded of item.excludePaths) {
      if (pathname.startsWith(excluded)) return false;
    }
  }

  if (item.matchPaths) {
    return item.matchPaths.some((mp) => pathname.startsWith(mp));
  }

  if (!item.path) {
    return false;
  }

  return pathname.startsWith(item.path);
}

function isItemOrChildActive(item, pathname) {
  if (isItemActive(item, pathname)) {
    return true;
  }

  return (
    item.children?.some(
      (child) => child.visible && isItemActive(child, pathname)
    ) ?? false
  );
}

function isResourceItemVisible(item) {
  if (item.isMoreMenu) {
    return item.children?.some((child) => child.visible) ?? false;
  }

  return item.visible;
}

export default function Sidebar({
  roleAdministrator,
  roleEditor,
  rolePrivateViewer,
  clientId,
  tier,
  userDisplayName,
  orgName,
  onSignOut,
}) {
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const location = useLocation();

  const handleDrawerOpen = useCallback(() => setOpen(true), []);
  const handleDrawerClose = useCallback(() => setOpen(false), []);

  const mainItems = useMemo(
    () =>
      getMainMenuItems({
        roleEditor,
        roleAdministrator,
        rolePrivateViewer,
        clientId,
      }),
    [roleAdministrator, roleEditor, rolePrivateViewer, clientId]
  );
  const visibleItems = mainItems.filter((item) => item.visible);
  const primaryItems = visibleItems.filter((item) => !item.isResourceGroup);
  const resourceGroup = visibleItems.find((item) => item.isResourceGroup);
  const resourceItems =
    resourceGroup?.children?.filter(isResourceItemVisible) || [];
  const workspaceName = orgName || userDisplayName || "Workspace";
  const workspaceInitial = workspaceName.trim().charAt(0).toUpperCase() || "W";
  const workspaceAvatarColor =
    stringAvatar(workspaceName).sx?.bgcolor || "#71717a";
  const workspaceTarget = roleAdministrator ? "/organization" : "/profile";
  const brandProps = open
    ? {
        component: Link,
        to: workspaceTarget,
        "aria-label": "Open workspace settings",
      }
    : {
        component: "button",
        type: "button",
        onClick: handleDrawerOpen,
        "aria-label": "Expand sidebar",
      };
  const utilityItems = [
    {
      label: "Help",
      icon: <HelpOutlineOutlinedIcon />,
      tooltip: "Help",
      onClick: () => setHelpOpen(true),
    },
    {
      path: "/profile",
      label: userDisplayName || "Profile",
      icon: <AccountBoxOutlinedIcon />,
      tooltip: "Your user account settings",
      exact: true,
    },
    ...(roleAdministrator
      ? [
          {
            path: "/organization",
            label: orgName || "Organization",
            icon: <BusinessIcon />,
            tooltip: "Your organization settings",
          },
        ]
      : []),
    {
      label: "Log out",
      icon: <ExitToAppIcon />,
      tooltip: "Log out",
      onClick: onSignOut,
    },
  ];

  return (
    <>
      <HelpDialog open={helpOpen} close={() => setHelpOpen(false)} />
      <StyledDrawer
        variant="permanent"
        isOpen={open}
        className="drawerMain"
        id="main_menu_drawer"
      >
        <SidebarBody>
          <HeaderRow>
            <Tooltip placement="right" title={open ? "" : workspaceName}>
              <BrandButton {...brandProps} isOpen={open}>
                <OrgAvatar avatarColor={workspaceAvatarColor}>
                  {workspaceInitial}
                </OrgAvatar>
                {open && (
                  <Typography
                    sx={{
                      minWidth: 0,
                      overflow: "hidden",
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      lineHeight: "18px",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {workspaceName}
                  </Typography>
                )}
              </BrandButton>
            </Tooltip>
            <CollapseButton
              type="button"
              isOpen={open}
              aria-label="Collapse sidebar"
              onClick={handleDrawerClose}
            >
              <SidebarPanelIcon />
            </CollapseButton>
          </HeaderRow>

          <SidebarNavScrollArea>
            <SidebarNavSection>
              {primaryItems.map((item) => (
                <SidebarNavItem
                  key={item.path}
                  item={item}
                  isOpen={open}
                  selected={isItemActive(item, location.pathname)}
                />
              ))}
            </SidebarNavSection>

            {resourceItems.length > 0 && (
              <SidebarNavGroup sx={{ mt: "14px" }}>
                <SidebarNavSectionHeader isOpen={open}>
                  Resources
                </SidebarNavSectionHeader>
                <SidebarNavSection>
                  {resourceItems.map((item) =>
                    item.isMoreMenu ? (
                      <SidebarNavMoreMenu
                        key={item.label}
                        item={item}
                        isOpen={open}
                        selected={isItemOrChildActive(item, location.pathname)}
                        isChildActive={(child) =>
                          isItemActive(child, location.pathname)
                        }
                      />
                    ) : (
                      <SidebarNavItem
                        key={item.path}
                        item={item}
                        isOpen={open}
                        selected={isItemActive(item, location.pathname)}
                      />
                    )
                  )}
                </SidebarNavSection>
              </SidebarNavGroup>
            )}
          </SidebarNavScrollArea>

          <BottomSection
            sx={tier === "FREE" ? { paddingBottom: "32px" } : undefined}
          >
            <SidebarUsageDisplay isOpen={open} />
            {utilityItems.map((item) => (
              <SidebarNavItem
                key={item.path || item.label}
                item={item}
                isOpen={open}
                selected={
                  item.path ? isItemActive(item, location.pathname) : false
                }
                onClick={item.onClick}
              />
            ))}
          </BottomSection>
        </SidebarBody>
      </StyledDrawer>
    </>
  );
}
